const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

/** Access token lives in memory only; the refresh token is an httpOnly cookie. */
let accessToken: string | null = null;
export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken() {
  return accessToken;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  /** Internal: set once we've already tried a token refresh for this call. */
  _retried?: boolean;
}

export async function apiFetch<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  // FormData (SCO-104 photo upload) goes through as-is — fetch sets its own
  // multipart/form-data content-type with the boundary, which we must not override.
  const isFormData = opts.body instanceof FormData;
  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers: {
      ...(opts.body !== undefined && !isFormData ? { 'content-type': 'application/json' } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    body: opts.body === undefined ? undefined : isFormData ? (opts.body as FormData) : JSON.stringify(opts.body),
    credentials: 'include',
  });

  if (res.status === 401 && !opts._retried && path !== '/auth/refresh') {
    const refreshed = await tryRefresh();
    if (refreshed) return apiFetch<T>(path, { ...opts, _retried: true });
  }

  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: unknown } })
      .error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? res.statusText, err?.details);
  }
  return payload as T;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const { accessToken: next } = await apiFetch<{ accessToken: string }>('/auth/refresh', {
      method: 'POST',
      _retried: true,
    });
    setAccessToken(next);
    return true;
  } catch {
    setAccessToken(null);
    return false;
  }
}

// --- Typed endpoint wrappers --------------------------------------------------------

export interface Me {
  user: { id: string; email: string };
  isPlatformAdmin: boolean;
  memberships: { unitId: string; roles: string[] }[];
}

export const auth = {
  register: (body: { email: string; password: string; displayName: string }) =>
    apiFetch<{ accessToken: string; user: Me['user'] }>('/auth/register', { method: 'POST', body }),
  login: (body: { email: string; password: string }) =>
    apiFetch<{ accessToken: string; user: Me['user'] }>('/auth/login', { method: 'POST', body }),
  logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),
  me: () => apiFetch<Me>('/auth/me'),
};

export type Role = 'ADMIN' | 'DEN_LEADER' | 'PARENT' | 'PACK_COMMITTEE_FINANCE' | 'COUNCIL';
export type UnitType = 'PACK' | 'TROOP';

export type CouncilArea =
  | 'finance'
  | 'roster'
  | 'advancement'
  | 'meetings'
  | 'calendar'
  | 'announcements';
export const COUNCIL_AREAS: CouncilArea[] = [
  'finance',
  'roster',
  'advancement',
  'meetings',
  'calendar',
  'announcements',
];
export interface UnitSummary {
  id: string;
  name: string;
  type: UnitType;
  roles: Role[];
  councilViewAreas: CouncilArea[];
  councilCanMessage: boolean;
  publicArchiveEnabled: boolean;
  defaultPostVisibility: string;
}
export interface UnitSettingsPatch {
  name?: string;
  publicArchiveEnabled?: boolean;
  defaultPostVisibility?: 'public' | 'members_only';
}

export interface Member {
  userId: string;
  email: string;
  displayName: string;
  roles: Role[];
  councilViewAreas: CouncilArea[];
  councilCanMessage: boolean;
  createdAt: string;
}
export interface MemberRolePatch {
  roles: Role[];
  councilViewAreas?: CouncilArea[];
  councilCanMessage?: boolean;
}

export const units = {
  mine: () => apiFetch<{ units: UnitSummary[] }>('/units/mine').then((r) => r.units),

  // Platform Admin
  listAll: () => apiFetch<{ units: (UnitSummary & { createdAt: string })[] }>('/units').then((r) => r.units),
  create: (body: { name: string; type: UnitType; firstAdminEmail?: string }) =>
    apiFetch<{
      unit: { id: string; name: string; type: UnitType };
      invite?: { invite: { token: string }; acceptUrl: string };
    }>('/units', { method: 'POST', body }),

  get: (unitId: string) =>
    apiFetch<{ unit: { id: string; name: string; type: UnitType } }>(`/units/${unitId}`).then(
      (r) => r.unit,
    ),
  rename: (unitId: string, name: string) =>
    apiFetch<{ unit: { id: string; name: string } }>(`/units/${unitId}`, {
      method: 'PATCH',
      body: { name },
    }).then((r) => r.unit),
  updateSettings: (unitId: string, patch: UnitSettingsPatch) =>
    apiFetch<{ unit: UnitSummary }>(`/units/${unitId}`, { method: 'PATCH', body: patch }).then(
      (r) => r.unit,
    ),

  members: (unitId: string) =>
    apiFetch<{ members: Member[] }>(`/units/${unitId}/members`).then((r) => r.members),
  addMember: (unitId: string, body: { email: string; roles: Role[] }) =>
    apiFetch<
      | { invited: false; userId: string; email: string; displayName: string; roles: Role[] }
      | { invited: true; invite: { token: string }; acceptUrl: string }
    >(`/units/${unitId}/members`, { method: 'POST', body }),
  setRoles: (unitId: string, userId: string, patch: MemberRolePatch) =>
    apiFetch<Member>(`/units/${unitId}/members/${userId}`, {
      method: 'PUT',
      body: patch,
    }),
  removeMember: (unitId: string, userId: string) =>
    apiFetch<void>(`/units/${unitId}/members/${userId}`, { method: 'DELETE' }),
  /** SCO-67 — full-unit JSON export (Admin only); returns the raw JSON string. */
  exportBundle: (unitId: string) => fetchText(`/units/${unitId}/export`),
};

export interface SocialLink {
  platform: string;
  url: string;
}

export interface UnitWebsiteInfo {
  status: 'draft' | 'published' | 'deactivated';
  subdomain: string | null;
  url: string | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  branding: {
    displayName: string;
    logoUrl: string | null;
    primaryColor: string | null;
    accentColor: string | null;
    socialLinks: SocialLink[];
    contactEmail: string | null;
    contactPhone: string | null;
    charterOrg: string | null;
    city: string | null;
    state: string | null;
  };
}

export interface BrandingPatch {
  displayName?: string;
  logoUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  socialLinks?: SocialLink[];
  contactEmail?: string | null;
  contactPhone?: string | null;
  charterOrg?: string | null;
  city?: string | null;
  state?: string | null;
}

/** SCO-82/83 — Unit public website status + branding config (Admin only). */
export const website = {
  get: (unitId: string) => apiFetch<UnitWebsiteInfo>(`/units/${unitId}/website`),
  updateBranding: (unitId: string, patch: BrandingPatch) =>
    apiFetch<UnitWebsiteInfo>(`/units/${unitId}/website/branding`, { method: 'PATCH', body: patch }),
  /** SCO-86 — safe to call again on an already-published Unit (resyncs, doesn't duplicate). */
  publish: (unitId: string) => apiFetch<UnitWebsiteInfo>(`/units/${unitId}/website/publish`, { method: 'POST' }),
};

// --- Public-site leader profiles (SCO-103, Admin only) -----------------------------
export interface LeaderRow {
  id: string;
  name: string;
  title: string;
  photoUrl: string | null;
  bio: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  sortOrder: number;
}
export interface LeaderInput {
  name: string;
  title: string;
  photoUrl?: string | null;
  bio?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}

export const leaders = {
  list: (unitId: string) =>
    apiFetch<{ leaders: LeaderRow[] }>(`/units/${unitId}/leaders`).then((r) => r.leaders),
  create: (unitId: string, body: LeaderInput) =>
    apiFetch<LeaderRow>(`/units/${unitId}/leaders`, { method: 'POST', body }),
  update: (unitId: string, leaderId: string, body: Partial<LeaderInput>) =>
    apiFetch<LeaderRow>(`/units/${unitId}/leaders/${leaderId}`, { method: 'PATCH', body }),
  move: (unitId: string, leaderId: string, direction: 'up' | 'down') =>
    apiFetch<{ leaders: LeaderRow[] }>(`/units/${unitId}/leaders/${leaderId}/move`, {
      method: 'POST',
      body: { direction },
    }).then((r) => r.leaders),
  remove: (unitId: string, leaderId: string) =>
    apiFetch<void>(`/units/${unitId}/leaders/${leaderId}`, { method: 'DELETE' }),
  /** SCO-104 — upload a photo file, returning a URL to pass as `photoUrl` on create/update. */
  uploadPhoto: (unitId: string, file: File) => {
    const body = new FormData();
    body.append('photo', file);
    return apiFetch<{ url: string }>(`/units/${unitId}/leaders/photo`, { method: 'POST', body });
  },
};

// --- Platform-wide settings (SCO-105, Platform Admin only) -------------------------
export interface PlatformSettingsInfo {
  heroImageUrl: string | null;
}
export const platformSettings = {
  get: () => apiFetch<PlatformSettingsInfo>('/platform-settings'),
  /** Uploads immediately and pushes it live to every Unit's site (network-wide option). */
  uploadHeroImage: (file: File) => {
    const body = new FormData();
    body.append('photo', file);
    return apiFetch<PlatformSettingsInfo>('/platform-settings/hero-image', { method: 'POST', body });
  },
  clearHeroImage: () =>
    apiFetch<PlatformSettingsInfo>('/platform-settings/hero-image', { method: 'DELETE' }),
};

export type CubRank = 'lion' | 'tiger' | 'wolf' | 'bear' | 'webelos' | 'arrow-of-light';

export interface DenRow {
  id: string;
  name: string;
  rank: CubRank | null;
  leaderCount: number;
  scoutCount: number;
}
export interface DenLeaderRow {
  userId: string;
  email: string;
  displayName: string;
}

export interface InviteRow {
  id: string;
  email: string;
  roles: Role[];
  token: string;
  createdAt: string;
  expiresAt: string;
  invitedByEmail: string;
  acceptUrl: string;
}
export interface InviteDescription {
  email: string;
  unitName: string;
  unitType: UnitType;
  roles: Role[];
  expired: boolean;
  accepted: boolean;
  accountExists: boolean;
}

export const invites = {
  list: (unitId: string) =>
    apiFetch<{ invites: InviteRow[] }>(`/units/${unitId}/invites`).then((r) => r.invites),
  create: (unitId: string, body: { email: string; roles: Role[] }) =>
    apiFetch<{ invite: Omit<InviteRow, 'invitedByEmail' | 'acceptUrl'>; acceptUrl: string }>(
      `/units/${unitId}/invites`,
      { method: 'POST', body },
    ),
  revoke: (unitId: string, inviteId: string) =>
    apiFetch<void>(`/units/${unitId}/invites/${inviteId}`, { method: 'DELETE' }),

  // Public
  describe: (token: string) => apiFetch<InviteDescription>(`/invites/${token}`),
  accept: (token: string, body: { password?: string; displayName?: string }) =>
    apiFetch<{ unitId: string; unitName: string; roles: Role[]; accessToken?: string }>(
      `/invites/${token}/accept`,
      { method: 'POST', body },
    ),
};

export interface RolloverPreview {
  fromYear: number;
  toYear: number;
  totalScouts: number;
  dens: {
    denId: string;
    denName: string;
    rank: string | null;
    scoutCount: number;
    suggestedTargetDenId: string | null;
    suggestedTargetDenName: string | null;
    graduating: boolean;
  }[];
}
export interface RolloverResult {
  fromYear: number;
  toYear: number;
  moved: number;
  stayed: number;
  graduated: number;
  dens: { denId: string; denName: string; scoutCount: number; targetDenId: string | null }[];
}

export const dens = {
  list: (unitId: string) =>
    apiFetch<{ programYear: number; dens: DenRow[] }>(`/units/${unitId}/dens`),
  rolloverPreview: (unitId: string, toYear?: number) =>
    apiFetch<RolloverPreview>(`/units/${unitId}/dens/rollover/preview`, {
      method: 'POST',
      body: toYear ? { toYear } : {},
    }),
  rolloverCommit: (
    unitId: string,
    body: { toYear?: number; mapping: Record<string, string | null> },
  ) => apiFetch<RolloverResult>(`/units/${unitId}/dens/rollover/commit`, { method: 'POST', body }),
  create: (unitId: string, name: string) =>
    apiFetch<{ den: { id: string; name: string } }>(`/units/${unitId}/dens`, {
      method: 'POST',
      body: { name },
    }).then((r) => r.den),
  patch: (unitId: string, denId: string, body: { name?: string; rank?: CubRank | null }) =>
    apiFetch<{ den: { id: string; name: string; rank: CubRank | null } }>(
      `/units/${unitId}/dens/${denId}`,
      { method: 'PATCH', body },
    ).then((r) => r.den),
  remove: (unitId: string, denId: string) =>
    apiFetch<void>(`/units/${unitId}/dens/${denId}`, { method: 'DELETE' }),
  leaders: (unitId: string, denId: string) =>
    apiFetch<{ leaders: DenLeaderRow[] }>(`/units/${unitId}/dens/${denId}/leaders`).then(
      (r) => r.leaders,
    ),
  addLeader: (unitId: string, denId: string, userId: string) =>
    apiFetch<{ leaders: DenLeaderRow[] }>(`/units/${unitId}/dens/${denId}/leaders`, {
      method: 'POST',
      body: { userId },
    }).then((r) => r.leaders),
  removeLeader: (unitId: string, denId: string, userId: string) =>
    apiFetch<void>(`/units/${unitId}/dens/${denId}/leaders/${userId}`, { method: 'DELETE' }),
};

export interface ScoutRow {
  id: string;
  firstName: string;
  lastName: string;
  dob: string | null;
  grade: string | null;
  den: { id: string; name: string } | null;
}
export interface ScoutDetail extends ScoutRow {
  guardians: { userId: string; email: string; displayName: string }[];
  denHistory: {
    denId: string;
    denName: string;
    programYear: number;
    startedAt: string;
    endedAt: string | null;
    current: boolean;
  }[];
}

export const scouts = {
  list: (unitId: string, params?: { denId?: string; unassigned?: boolean }) => {
    const qs = new URLSearchParams();
    if (params?.denId) qs.set('denId', params.denId);
    if (params?.unassigned) qs.set('unassigned', 'true');
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch<{ programYear: number; scouts: ScoutRow[] }>(`/units/${unitId}/scouts${suffix}`);
  },
  get: (unitId: string, scoutId: string) =>
    apiFetch<ScoutDetail>(`/units/${unitId}/scouts/${scoutId}`),
  create: (
    unitId: string,
    body: { firstName: string; lastName: string; grade?: string | null; dob?: string | null; denId?: string },
  ) => apiFetch<ScoutDetail>(`/units/${unitId}/scouts`, { method: 'POST', body }),
  update: (
    unitId: string,
    scoutId: string,
    body: Partial<{ firstName: string; lastName: string; grade: string | null; dob: string | null }>,
  ) => apiFetch<ScoutDetail>(`/units/${unitId}/scouts/${scoutId}`, { method: 'PATCH', body }),
  remove: (unitId: string, scoutId: string) =>
    apiFetch<void>(`/units/${unitId}/scouts/${scoutId}`, { method: 'DELETE' }),
  assignDen: (unitId: string, scoutId: string, denId: string | null) =>
    apiFetch<ScoutDetail>(`/units/${unitId}/scouts/${scoutId}/den`, {
      method: 'PUT',
      body: { denId },
    }),
  addGuardian: (unitId: string, scoutId: string, email: string) =>
    apiFetch<ScoutDetail>(`/units/${unitId}/scouts/${scoutId}/guardians`, {
      method: 'POST',
      body: { email },
    }),
  removeGuardian: (unitId: string, scoutId: string, userId: string) =>
    apiFetch<void>(`/units/${unitId}/scouts/${scoutId}/guardians/${userId}`, { method: 'DELETE' }),
};

// --- CSV roster import (SCO-41 / SCO-42) ------------------------------------------
export type ImportRowStatus = 'new' | 'update' | 'conflict' | 'unchanged' | 'error';
export interface ImportFieldChange {
  field: 'grade' | 'dob' | 'den';
  from: string | null;
  to: string | null;
  conflict: boolean;
}
export interface ImportGuardianPlan {
  email: string;
  name: string | null;
  accountExists: boolean;
  alreadyLinked: boolean;
}
export interface ImportRow {
  rowNumber: number;
  firstName: string;
  lastName: string;
  grade: string | null;
  dob: string | null;
  denName: string | null;
  denId: string | null;
  matchScoutId: string | null;
  status: ImportRowStatus;
  changes: ImportFieldChange[];
  guardians: ImportGuardianPlan[];
  errors: string[];
}
export interface ImportPreview {
  columns: string[];
  dens: { id: string; name: string }[];
  rows: ImportRow[];
  summary: Record<ImportRowStatus, number> & { total: number };
}
export type ConflictField = 'grade' | 'dob' | 'den';
export type ConflictDecision = 'skip' | Partial<Record<ConflictField, 'csv' | 'existing'>>;
export interface ImportCommitResult {
  created: number;
  updated: number;
  skipped: number;
  guardiansLinked: number;
  guardiansInvited: number;
  guardiansSkipped: number;
  rows: { rowNumber: number; outcome: string; scoutId?: string }[];
}

/** GET the template as text (needs the auth header, so not a plain link). */
async function fetchText(path: string): Promise<string> {
  const res = await fetch(`${BASE}${path}`, {
    headers: getAccessToken() ? { authorization: `Bearer ${getAccessToken()}` } : {},
    credentials: 'include',
  });
  if (!res.ok) throw new ApiError(res.status, 'error', res.statusText);
  return res.text();
}

export const rosterImport = {
  template: (unitId: string) => fetchText(`/units/${unitId}/roster-import/template`),
  preview: (unitId: string, csv: string) =>
    apiFetch<ImportPreview>(`/units/${unitId}/roster-import/preview`, {
      method: 'POST',
      body: { csv },
    }),
  commit: (unitId: string, csv: string, decisions?: Record<string, ConflictDecision>) =>
    apiFetch<ImportCommitResult>(`/units/${unitId}/roster-import/commit`, {
      method: 'POST',
      body: { csv, decisions },
    }),
};

export interface CatalogRequirement {
  number: string;
  text: string;
}
export interface CatalogEntry {
  sourceId: string;
  name: string;
  rank: string;
  ranks: string[];
  area: string | null;
  category: string;
  officialUrl: string | null;
  requirements: CatalogRequirement[] | null;
  hasNoPublishedRequirements: boolean;
  isShootingSports: boolean;
  textRights: string | null;
  supplyList: string[] | null;
  customized: boolean;
  hidden: boolean;
  note: string | null;
  active: boolean;
  retiredAt: string | null;
  supersededBy: string | null;
}
export interface CatalogSyncStatus {
  sourceTag: string | null;
  lastRunAt?: string | null;
  lastOkAt: string | null;
  lastError: string | null;
  adventureCount?: number;
  requirementSetCount?: number;
  stale: boolean;
}
export interface CatalogResponse {
  adventures: CatalogEntry[];
  sync: CatalogSyncStatus;
  attribution: string;
}

export const catalog = {
  get: (
    unitId: string,
    params?: { rank?: string; category?: string; includeHidden?: boolean; includeRetired?: boolean },
  ) => {
    const qs = new URLSearchParams();
    if (params?.rank) qs.set('rank', params.rank);
    if (params?.category) qs.set('category', params.category);
    if (params?.includeHidden) qs.set('includeHidden', 'true');
    if (params?.includeRetired) qs.set('includeRetired', 'true');
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch<CatalogResponse>(`/units/${unitId}/catalog${suffix}`);
  },
  override: (
    unitId: string,
    sourceId: string,
    patch: {
      supplyList?: string[] | null;
      nameOverride?: string | null;
      requirementsOverride?: CatalogRequirement[] | null;
      hidden?: boolean;
      note?: string | null;
    },
  ) => apiFetch<CatalogEntry>(`/units/${unitId}/catalog/${sourceId}`, { method: 'PATCH', body: patch }),
  clearOverride: (unitId: string, sourceId: string) =>
    apiFetch<void>(`/units/${unitId}/catalog/${sourceId}/override`, { method: 'DELETE' }),
  sync: (unitId: string) =>
    apiFetch<{ ok: boolean; adventureCount: number; requirementSetCount: number; errors: string[] }>(
      `/units/${unitId}/catalog/sync`,
      { method: 'POST' },
    ),
};

export interface MeetingListItem {
  id: string;
  denId: string | null; // null = pack-wide (SCO-92)
  denName: string;
  date: string;
  startTime: string | null;
  title: string | null;
  finalized: boolean;
  adventureCount: number;
  adventures: string[];
}
export interface MeetingSupply {
  id: string;
  item: string;
  quantity: string | null;
  source: 'auto' | 'manual';
  fromAdventures: string[];
  editedManually: boolean;
}
export interface MeetingAdventureEntry {
  sourceId: string;
  name: string;
  category: string | null;
  officialUrl: string | null;
  requirements: CatalogRequirement[] | null;
  hasNoPublishedRequirements: boolean;
  isShootingSports: boolean;
  textRights: string | null;
  /** SCO-73 — requirement numbers this meeting covered; null = the whole adventure. */
  coveredRequirements: string[] | null;
}
export interface MeetingDetail {
  id: string;
  denId: string | null; // null = pack-wide (SCO-92)
  denName: string;
  denRank: CubRank | null;
  date: string;
  startTime: string | null;
  title: string | null;
  notes: string | null;
  finalized: boolean;
  finalizedAt: string | null;
  adventures: MeetingAdventureEntry[];
  supplies: MeetingSupply[];
  advancementRecorded: { requirements: number; scouts: number };
  attribution: string;
}
export interface AvailableAdventure {
  sourceId: string;
  name: string;
  category: string;
  officialUrl: string | null;
  supplyCount: number;
  checked: boolean;
}

export const meetings = {
  list: (unitId: string, when?: 'upcoming' | 'past', denId?: string) => {
    const qs = new URLSearchParams();
    if (when) qs.set('when', when);
    if (denId) qs.set('denId', denId);
    const s = qs.toString() ? `?${qs}` : '';
    return apiFetch<{ meetings: MeetingListItem[] }>(`/units/${unitId}/meetings${s}`).then(
      (r) => r.meetings,
    );
  },
  get: (unitId: string, id: string) => apiFetch<MeetingDetail>(`/units/${unitId}/meetings/${id}`),
  create: (
    unitId: string,
    // Omit/null denId for a pack-wide meeting (SCO-92, Admin only).
    body: { denId?: string | null; date: string; startTime?: string; title?: string },
  ) => apiFetch<MeetingDetail>(`/units/${unitId}/meetings`, { method: 'POST', body }),
  update: (
    unitId: string,
    id: string,
    body: Partial<{
      date: string;
      startTime: string | null;
      title: string | null;
      notes: string | null;
      finalized: boolean;
    }>,
  ) => apiFetch<MeetingDetail>(`/units/${unitId}/meetings/${id}`, { method: 'PATCH', body }),
  remove: (unitId: string, id: string) =>
    apiFetch<void>(`/units/${unitId}/meetings/${id}`, { method: 'DELETE' }),
  availableAdventures: (unitId: string, id: string) =>
    apiFetch<{ denRank: CubRank | null; adventures: AvailableAdventure[] }>(
      `/units/${unitId}/meetings/${id}/available-adventures`,
    ),
  setAdventure: (unitId: string, id: string, sourceId: string, checked: boolean) =>
    apiFetch<MeetingDetail>(`/units/${unitId}/meetings/${id}/adventures/${sourceId}`, {
      method: checked ? 'PUT' : 'DELETE',
    }),
  setAdventureRequirements: (
    unitId: string,
    id: string,
    sourceId: string,
    requirementNumbers: string[] | null,
  ) =>
    apiFetch<MeetingDetail>(
      `/units/${unitId}/meetings/${id}/adventures/${sourceId}/requirements`,
      { method: 'PUT', body: { requirementNumbers } },
    ),
  addSupply: (unitId: string, id: string, body: { item: string; quantity?: string }) =>
    apiFetch<MeetingDetail>(`/units/${unitId}/meetings/${id}/supplies`, { method: 'POST', body }),
  updateSupply: (
    unitId: string,
    id: string,
    supplyId: string,
    body: { item?: string; quantity?: string | null },
  ) =>
    apiFetch<MeetingDetail>(`/units/${unitId}/meetings/${id}/supplies/${supplyId}`, {
      method: 'PATCH',
      body,
    }),
  deleteSupply: (unitId: string, id: string, supplyId: string) =>
    apiFetch<MeetingDetail>(`/units/${unitId}/meetings/${id}/supplies/${supplyId}`, {
      method: 'DELETE',
    }),
};

// --- Calendar & Events (SCO-63/64/65/66) ----------------------------------------
export interface CalendarEvent {
  kind: 'event';
  id: string;
  denId: string | null;
  denName: string | null;
  scope: 'pack' | 'den';
  title: string;
  description: string | null;
  location: string | null;
  startsAt: string;
  endsAt: string | null;
  rsvpRequired: boolean;
  capacity: number | null;
}
export type RsvpResponse = 'yes' | 'no' | 'maybe';
export interface RsvpLine {
  scoutId: string | null;
  response: RsvpResponse;
  waitlisted: boolean;
}
export interface EventAttendee {
  userName: string;
  userEmail: string;
  scoutName: string | null;
  response: RsvpResponse;
  waitlisted: boolean;
}
export interface EventDetail extends CalendarEvent {
  counts: { yes: number; waitlisted: number; no: number; maybe: number };
  spotsLeft: number | null;
  mine: RsvpLine[];
  eligibleScouts: { id: string; name: string }[];
  attendees?: EventAttendee[];
}
export interface EventInput {
  title: string;
  description?: string | null;
  location?: string | null;
  startsAt: string;
  endsAt?: string | null;
  denId?: string | null;
  rsvpRequired?: boolean;
  capacity?: number | null;
}

export const events = {
  list: (unitId: string, when?: 'upcoming' | 'past', denId?: string) => {
    const qs = new URLSearchParams();
    if (when) qs.set('when', when);
    if (denId) qs.set('denId', denId);
    const s = qs.toString() ? `?${qs}` : '';
    return apiFetch<{ events: CalendarEvent[] }>(`/units/${unitId}/events${s}`).then((r) => r.events);
  },
  get: (unitId: string, id: string) =>
    apiFetch<EventDetail>(`/units/${unitId}/events/${id}`),
  create: (unitId: string, body: EventInput) =>
    apiFetch<CalendarEvent>(`/units/${unitId}/events`, { method: 'POST', body }),
  update: (unitId: string, id: string, body: Partial<EventInput>) =>
    apiFetch<CalendarEvent>(`/units/${unitId}/events/${id}`, { method: 'PATCH', body }),
  remove: (unitId: string, id: string) =>
    apiFetch<void>(`/units/${unitId}/events/${id}`, { method: 'DELETE' }),
  rsvp: (unitId: string, id: string, body: { scoutId: string | null; response: RsvpResponse }) =>
    apiFetch<EventDetail>(`/units/${unitId}/events/${id}/rsvp`, { method: 'PUT', body }),
  clearRsvp: (unitId: string, id: string, scoutId: string | null) => {
    const qs = scoutId ? `?scoutId=${encodeURIComponent(scoutId)}` : '';
    return apiFetch<EventDetail>(`/units/${unitId}/events/${id}/rsvp${qs}`, { method: 'DELETE' });
  },
};

// --- Finance: dues/fee catalog (SCO-22) + adjustments (SCO-25) ------------------
export type FeeBasis = 'per_scout' | 'per_family';
export interface FeeItem {
  id: string;
  name: string;
  amountCents: number;
  basis: FeeBasis;
  dueDate: string | null;
  retired: boolean;
}
export interface FeeItemInput {
  name: string;
  amountCents: number;
  basis: FeeBasis;
  dueDate?: string | null;
}
export interface DuesLine {
  feeItemId: string | null;
  name: string;
  basis: FeeBasis | 'adjustment';
  baseCents: number;
  adjustmentCents: number;
  owedCents: number;
  note: string | null;
}
export interface CreditLine {
  fundraiserId: string;
  name: string;
  amountCents: number;
}
export interface ScoutDues {
  scoutId: string;
  firstName: string;
  lastName: string;
  lines: DuesLine[];
  totalCents: number;
  paidCents: number;
  creditLines: CreditLine[];
  creditsCents: number;
  balanceCents: number;
}
export interface Fundraiser {
  id: string;
  name: string;
  itemName: string | null;
  unitPriceCents: number;
  commissionRatePct: number;
  startsOn: string | null;
  endsOn: string | null;
  closed: boolean;
  itemsSold?: number;
  grossCents?: number;
  commissionCents?: number;
  sellers?: number;
}
export interface FundraiserInput {
  name: string;
  itemName?: string | null;
  unitPriceCents: number;
  commissionRatePct: number;
  startsOn?: string | null;
  endsOn?: string | null;
}
export interface FundraiserSaleRow {
  scoutId: string;
  firstName: string;
  lastName: string;
  quantity: number;
  grossCents: number;
  commissionCents: number;
  note: string | null;
}
export interface ReportFilters {
  denId?: string;
  scoutId?: string;
  from?: string;
  to?: string;
  fundraiserId?: string;
}
export interface FinanceReport {
  scoutCount: number;
  dues: { chargedCents: number; paidCents: number; creditsCents: number; outstandingCents: number };
  cash: { collectedInRangeCents: number; byMethod: { method: string; cents: number }[] };
  fundraisers: {
    id: string;
    name: string;
    itemName: string | null;
    unitPriceCents: number;
    commissionRatePct: number;
    itemsSold: number;
    grossCents: number;
    commissionCents: number;
    sellers: number;
  }[];
  commissionByScout: { scoutId: string; name: string; denName: string | null; commissionCents: number }[];
  commissionByDen: { denId: string | null; denName: string; commissionCents: number }[];
  ledger: {
    scoutId: string;
    name: string;
    denName: string | null;
    chargedCents: number;
    paidCents: number;
    creditsCents: number;
    balanceCents: number;
  }[];
}
export type PaymentMethod = 'cash' | 'check' | 'other' | 'square';
export interface Payment {
  id: string;
  scoutId: string;
  amountCents: number;
  method: PaymentMethod;
  paidAt: string;
  note: string | null;
  reference: string | null;
  reversed: boolean;
  reversalReason: string | null;
  recordedAt: string;
}
export interface PaymentInput {
  scoutId: string;
  amountCents: number;
  method: PaymentMethod;
  paidAt: string;
  note?: string | null;
  reference?: string | null;
}
export interface Receipt {
  receiptId: string;
  unitName: string;
  scoutName: string;
  amountCents: number;
  method: PaymentMethod;
  paidAt: string;
  reference: string | null;
  note: string | null;
  reversed: boolean;
  issuedAt: string;
}
export interface FeeAdjustment {
  id: string;
  feeItemId: string | null;
  feeItemName: string | null;
  amountCents: number;
  reason: string;
  createdAt: string;
}

export const finance = {
  fees: {
    list: (unitId: string, includeRetired = false) =>
      apiFetch<{ feeItems: FeeItem[] }>(
        `/units/${unitId}/finance/fees${includeRetired ? '?includeRetired=true' : ''}`,
      ).then((r) => r.feeItems),
    create: (unitId: string, body: FeeItemInput) =>
      apiFetch<FeeItem>(`/units/${unitId}/finance/fees`, { method: 'POST', body }),
    update: (unitId: string, id: string, body: Partial<FeeItemInput>) =>
      apiFetch<FeeItem>(`/units/${unitId}/finance/fees/${id}`, { method: 'PATCH', body }),
    retire: (unitId: string, id: string, retired: boolean) =>
      apiFetch<FeeItem>(`/units/${unitId}/finance/fees/${id}/retire`, {
        method: 'POST',
        body: { retired },
      }),
  },
  dues: (unitId: string) =>
    apiFetch<{ dues: ScoutDues[] }>(`/units/${unitId}/finance/dues`).then((r) => r.dues),
  /** SCO-68 — paid/unpaid only, safe for Den Leaders. */
  paidStatus: (unitId: string) =>
    apiFetch<{ scouts: { scoutId: string; firstName: string; lastName: string; paid: boolean; owes: boolean }[] }>(
      `/units/${unitId}/finance/paid-status`,
    ).then((r) => r.scouts),
  adjustments: {
    list: (unitId: string, scoutId: string) =>
      apiFetch<{ adjustments: FeeAdjustment[] }>(
        `/units/${unitId}/finance/adjustments?scoutId=${encodeURIComponent(scoutId)}`,
      ).then((r) => r.adjustments),
    add: (
      unitId: string,
      body: { scoutId: string; feeItemId?: string | null; amountCents: number; reason: string },
    ) => apiFetch<{ id: string }>(`/units/${unitId}/finance/adjustments`, { method: 'POST', body }),
    remove: (unitId: string, id: string) =>
      apiFetch<void>(`/units/${unitId}/finance/adjustments/${id}`, { method: 'DELETE' }),
  },
  payments: {
    list: (unitId: string, scoutId: string) =>
      apiFetch<{ payments: Payment[] }>(
        `/units/${unitId}/finance/payments?scoutId=${encodeURIComponent(scoutId)}`,
      ).then((r) => r.payments),
    record: (unitId: string, body: PaymentInput) =>
      apiFetch<Payment>(`/units/${unitId}/finance/payments`, { method: 'POST', body }),
    update: (unitId: string, id: string, body: Partial<Omit<PaymentInput, 'scoutId'>>) =>
      apiFetch<Payment>(`/units/${unitId}/finance/payments/${id}`, { method: 'PATCH', body }),
    reverse: (unitId: string, id: string, reason: string) =>
      apiFetch<Payment>(`/units/${unitId}/finance/payments/${id}/reverse`, {
        method: 'POST',
        body: { reason },
      }),
    receipt: (unitId: string, id: string) =>
      apiFetch<Receipt>(`/units/${unitId}/finance/payments/${id}/receipt`),
  },
  fundraisers: {
    list: (unitId: string) =>
      apiFetch<{ fundraisers: Fundraiser[] }>(`/units/${unitId}/finance/fundraisers`).then(
        (r) => r.fundraisers,
      ),
    create: (unitId: string, body: FundraiserInput) =>
      apiFetch<Fundraiser>(`/units/${unitId}/finance/fundraisers`, { method: 'POST', body }),
    update: (unitId: string, id: string, body: Partial<FundraiserInput> & { closed?: boolean }) =>
      apiFetch<Fundraiser>(`/units/${unitId}/finance/fundraisers/${id}`, { method: 'PATCH', body }),
    sales: (unitId: string, id: string) =>
      apiFetch<{ fundraiser: Fundraiser; sales: FundraiserSaleRow[] }>(
        `/units/${unitId}/finance/fundraisers/${id}/sales`,
      ),
    setSale: (
      unitId: string,
      id: string,
      body: { scoutId: string; quantity: number; note?: string | null },
    ) =>
      apiFetch<{ fundraiser: Fundraiser; sales: FundraiserSaleRow[] }>(
        `/units/${unitId}/finance/fundraisers/${id}/sales`,
        { method: 'PUT', body },
      ),
  },
  reportQuery: (f: ReportFilters) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v) qs.set(k, v);
    return qs.toString();
  },
  report: (unitId: string, f: ReportFilters = {}) => {
    const qs = finance.reportQuery(f);
    return apiFetch<FinanceReport>(`/units/${unitId}/finance/report${qs ? `?${qs}` : ''}`);
  },
  reportCsv: (unitId: string, f: ReportFilters, section: 'ledger' | 'payments' | 'fundraisers') => {
    const qs = finance.reportQuery({ ...f });
    return fetchText(
      `/units/${unitId}/finance/report.csv?section=${section}${qs ? `&${qs}` : ''}`,
    );
  },
};

// --- Announcements / blog archive (SCO-54–62) ---------------------------------
export type PostVisibility = 'public' | 'members_only';
export interface AnnouncementPost {
  id: string;
  title: string;
  body: string;
  authorId: string;
  authorName: string;
  denId: string | null;
  denName: string | null;
  visibility: PostVisibility;
  sentMessageRef: string | null;
  publishedAt: string;
  editedAt: string | null;
}
export interface PostInput {
  title: string;
  body: string;
  denId?: string | null;
  visibility?: PostVisibility;
}

export interface PublicArchive {
  enabled: boolean;
  unitName: string | null;
  posts: { id: string; title: string; body: string; authorName: string; publishedAt: string }[];
}

export const announcements = {
  public: (unitId: string, params?: { from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const s = qs.toString() ? `?${qs}` : '';
    return apiFetch<PublicArchive>(`/public/units/${unitId}/announcements${s}`);
  },
  list: (unitId: string, params?: { denId?: string; from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (params?.denId) qs.set('denId', params.denId);
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const s = qs.toString() ? `?${qs}` : '';
    return apiFetch<{ posts: AnnouncementPost[] }>(`/units/${unitId}/announcements${s}`).then(
      (r) => r.posts,
    );
  },
  get: (unitId: string, id: string) =>
    apiFetch<AnnouncementPost>(`/units/${unitId}/announcements/${id}`),
  create: (unitId: string, body: PostInput) =>
    apiFetch<AnnouncementPost>(`/units/${unitId}/announcements`, { method: 'POST', body }),
  update: (unitId: string, id: string, body: Partial<PostInput>) =>
    apiFetch<AnnouncementPost>(`/units/${unitId}/announcements/${id}`, { method: 'PATCH', body }),
  remove: (unitId: string, id: string) =>
    apiFetch<void>(`/units/${unitId}/announcements/${id}`, { method: 'DELETE' }),
};

// --- Messaging (SCO-51/52) --------------------------------------------------------
export type MessageStatusFilter =
  | 'dues_unpaid'
  | 'dues_paid'
  | 'fundraiser_unsubmitted'
  | 'rsvp_pending';

export interface MessageTemplate {
  key: string;
  label: string;
  subject: string;
  body: string;
}
export interface RecipientFilter {
  denIds?: string[];
  statuses?: MessageStatusFilter[];
  eventId?: string;
}
export interface ResolvedRecipient {
  email: string;
  userId: string | null;
  userName: string;
  scouts: { id: string; name: string; denName: string | null }[];
}
export interface ResolvedRecipients {
  filter: { denIds: string[]; statuses: MessageStatusFilter[]; eventId: string | null };
  scoutCount: number;
  recipientCount: number;
  scoutsWithoutContact: number;
  recipients: ResolvedRecipient[];
}
export interface MessageDelivery {
  queued: number;
  sent: number;
  bounced: number;
}
export interface MessageSummary {
  id: string;
  subject: string;
  body: string;
  senderName: string;
  denFilter: string[];
  statusFilter: MessageStatusFilter[];
  recipientCount: number;
  savedPostId: string | null;
  deliveryProvider: string;
  sentAt: string;
  delivery: MessageDelivery;
}
export interface MessageDetail extends MessageSummary {
  eventId: string | null;
  recipients: { email: string; scoutId: string | null; deliveryStatus: string }[];
}
export interface SendMessagePayload {
  subject: string;
  body: string;
  filter: RecipientFilter;
  saveToArchive?: boolean;
  archiveVisibility?: PostVisibility;
}

export const messaging = {
  templates: (unitId: string) =>
    apiFetch<{ templates: MessageTemplate[] }>(`/units/${unitId}/messages/templates`).then(
      (r) => r.templates,
    ),
  previewRecipients: (unitId: string, filter: RecipientFilter) =>
    apiFetch<ResolvedRecipients>(`/units/${unitId}/messages/recipients`, {
      method: 'POST',
      body: filter,
    }),
  list: (unitId: string) =>
    apiFetch<{ messages: MessageSummary[] }>(`/units/${unitId}/messages`).then((r) => r.messages),
  get: (unitId: string, id: string) =>
    apiFetch<MessageDetail>(`/units/${unitId}/messages/${id}`),
  send: (unitId: string, body: SendMessagePayload) =>
    apiFetch<MessageDetail>(`/units/${unitId}/messages`, { method: 'POST', body }),
};

export function centsToUSD(c: number): string {
  return (c / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
export function usdToCents(s: string): number {
  return Math.round(parseFloat(s.replace(/[^0-9.-]/g, '')) * 100) || 0;
}

export interface AdvancementRequirement {
  number: string;
  text: string;
  completed: boolean;
  completedAt: string | null;
  viaMeeting: string | null;
}
export interface AdvancementAdventure {
  sourceId: string;
  name: string;
  category: string;
  officialUrl: string | null;
  hasNoPublishedRequirements: boolean;
  isShootingSports: boolean;
  textRights: string | null;
  requirements: AdvancementRequirement[];
  completedCount: number;
  total: number;
  complete: boolean;
}
export interface AdvancementSummary {
  adventuresComplete: number;
  requiredComplete: number;
  requiredTotal: number;
  electivesComplete: number;
  minElectives: number;
  rank: CubRank | null;
  rankLabel: string | null;
  readyToAward: boolean;
}
export interface ScoutAdvancement {
  scout: { id: string; firstName: string; lastName: string; rank: CubRank | null };
  adventures: AdvancementAdventure[];
  summary: AdvancementSummary;
  attribution: string;
}
export interface ScoutSummary extends AdvancementSummary {
  scoutId: string;
  firstName: string;
  lastName: string;
  denName: string | null;
}

export const advancement = {
  get: (unitId: string, scoutId: string) =>
    apiFetch<ScoutAdvancement>(`/units/${unitId}/scouts/${scoutId}/advancement`),
  set: (unitId: string, scoutId: string, sourceId: string, set: Record<string, boolean>) =>
    apiFetch<ScoutAdvancement>(`/units/${unitId}/scouts/${scoutId}/advancement/${sourceId}`, {
      method: 'PATCH',
      body: { set },
    }),
  applyMeeting: (unitId: string, meetingId: string, scoutIds: string[]) =>
    apiFetch<{ added: number; scouts: number }>(
      `/units/${unitId}/meetings/${meetingId}/apply-advancement`,
      { method: 'POST', body: { scoutIds } },
    ),
  summaries: (unitId: string) =>
    apiFetch<{ scouts: ScoutSummary[] }>(`/units/${unitId}/advancement/summaries`).then(
      (r) => r.scouts,
    ),
  mine: (unitId: string) =>
    apiFetch<{ scouts: ScoutSummary[] }>(`/units/${unitId}/advancement/mine`).then((r) => r.scouts),
};

export const RANK_LABELS: Record<string, string> = {
  lion: 'Lion',
  tiger: 'Tiger',
  wolf: 'Wolf',
  bear: 'Bear',
  webelos: 'Webelos',
  'arrow-of-light': 'Arrow of Light',
};
export const RANK_ORDER = ['lion', 'tiger', 'wolf', 'bear', 'webelos', 'arrow-of-light'];

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'Admin',
  DEN_LEADER: 'Den Leader',
  PARENT: 'Parent',
  PACK_COMMITTEE_FINANCE: 'Pack Committee (Finance)',
  COUNCIL: 'Council',
};
